import { useMutation } from '@apollo/client'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'

import {
  IEditPrepSettingsDefaultData,
  IEditPrepSettingsDefaultVariables,
  IPrepSettingValueInput,
  IScopeOption,
  editPrepSettingsDefault,
} from '../gql'
import { ISettingRule } from '../hooks/usePrepSettings'
import RuleRows, { IEditableRule } from './RuleRows'
import validateRules from './validateRules'

interface IEditPrepSettingsDefaultModalProps {
  visible: boolean
  onClose: () => void
  settingName: string
  currentRules: ISettingRule[]
  scopes: IScopeOption[]
  scopesLoading: boolean
  scopesError: boolean
  onSuccess?: () => void
}

const generateId = () => Math.random().toString(36).substring(2, 9)

const serialize = (
  rules: { locationGroupId: number | null; value: string }[],
) => JSON.stringify(rules.map((rule) => [rule.locationGroupId, rule.value]))

const EditPrepSettingsDefaultModal = ({
  visible,
  onClose,
  settingName,
  currentRules,
  scopes,
  scopesLoading,
  scopesError,
  onSuccess,
}: IEditPrepSettingsDefaultModalProps) => {
  const [rules, setRules] = useState<IEditableRule[]>([])
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)

  const [editPrepSettingsDefaultMutation] = useMutation<
    IEditPrepSettingsDefaultData,
    IEditPrepSettingsDefaultVariables
  >(editPrepSettingsDefault)

  useEffect(() => {
    if (visible) {
      setRules(
        currentRules.map((rule) => ({
          id: generateId(),
          locationGroupId: rule.locationGroupId,
          value: rule.value.toString(),
        })),
      )
      setError(null)
      setSuccess(false)
    }
  }, [visible, currentRules])

  const initialRules = serialize(
    currentRules.map((rule) => ({
      locationGroupId: rule.locationGroupId,
      value: rule.value.toString(),
    })),
  )

  const hasChanges = () => serialize(rules) !== initialRules

  const handleClose = () => {
    if (hasChanges() && !success) {
      setShowConfirmDialog(true)
    } else {
      onClose()
    }
  }

  const handleConfirmDiscard = () => {
    setShowConfirmDialog(false)
    onClose()
  }

  const handleContinueEditing = () => {
    setShowConfirmDialog(false)
  }

  const handleSave = async () => {
    const validationError = validateRules(rules)
    if (validationError) {
      setError(validationError)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const valuesToSave: IPrepSettingValueInput[] = rules.map((rule) => ({
        value: parseFloat(rule.value),
        locationGroupId: rule.locationGroupId,
      }))

      await editPrepSettingsDefaultMutation({
        variables: {
          iName: settingName,
          iValues: valuesToSave,
        },
      })

      setSuccess(true)

      if (onSuccess) {
        onSuccess()
      }

      setTimeout(() => {
        onClose()
        setSuccess(false)
        setError(null)
        setIsLoading(false)
      }, 1500)
    } catch (err) {
      setError('Failed to update. Please try again.')
      setIsLoading(false)
    }
  }

  return (
    <>
      <Dialog open={visible} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{settingName}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography sx={{ mb: 2, fontSize: 13.5, color: 'text.secondary' }}>
              Standing increase, in effect until a scheduled change takes over
              its dates. A group with no rule is left unchanged.
            </Typography>

            {scopesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <RuleRows
                rules={rules}
                scopes={scopes}
                disabled={isLoading || success}
                onChange={setRules}
              />
            )}

            {scopesError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                Could not load the list of states. Reload the page before adding
                a rule.
              </Alert>
            )}

            <Typography sx={{ mt: 2, fontSize: 12.5, color: 'text.secondary' }}>
              Most specific wins: single store &gt; state / region &gt; all
              locations.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Successfully updated
              </Alert>
            )}

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1,
                mt: 4,
              }}
            >
              <Button
                onClick={handleClose}
                variant="outlined"
                disabled={isLoading}
              >
                Close
              </Button>
              <Button
                onClick={handleSave}
                variant="contained"
                disabled={success || isLoading || scopesLoading || scopesError}
              >
                Save
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog open={showConfirmDialog} onClose={handleContinueEditing}>
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved changes. Do you want to discard them or continue
            editing?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleContinueEditing} variant="outlined">
            Continue Editing
          </Button>
          <Button
            onClick={handleConfirmDiscard}
            variant="contained"
            color="error"
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default EditPrepSettingsDefaultModal
